import {Severity, Show} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {clamp} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {DeterminateProgressBarProps} from './DeterminateProgressBarProps';
import './ProgressBar.css';

const barContainerVariants = cva('relative flex w-full overflow-hidden');

const barVariants = cva('relative transition-[width] duration-100 ease-in-out', {
  variants: {
    small: {
      true: 'h-[calc(var(--eag-space-step,0.25)*1rem)]',
      false: 'h-[calc(var(--eag-space-step,0.25)*2rem)]',
    },
  },
});

const barAnimationVariants = cva(
  'h-full w-full origin-[0%_50%] animate-[indeterminateAnimation_1s_linear_infinite] bg-[#00000050]'
);

export type IndeterminateProgressBarProps = {
  percentage?: number;
  indeterminate?: boolean;
  severity?: Severity;
  small?: boolean;
};

export type ProgressBarProps = (DeterminateProgressBarProps | IndeterminateProgressBarProps) &
  TestIdProps;

export function ProgressBar(props: ProgressBarProps) {
  const theme = useComponentsTheme();

  return (
    <div
      className={cn(barContainerVariants())}
      data-testid={suffixTestId('progressBarWrapper', props)}
    >
      <div
        className={cn(barVariants({small: props.small ?? false}))}
        style={{
          backgroundColor: theme.colors.severity[props.severity ?? 'informational'],
          width: `${clampPercentage(props.percentage ?? 1) * 100}%`,
        }}
        data-testid={suffixTestId('progressBar', props)}
      >
        <Show when={props.indeterminate}>
          <div className={cn(barAnimationVariants())} />
        </Show>
      </div>
    </div>
  );
}

const clampPercentage = clamp(0, 1);
